import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  allocatedPercentage,
  computeAllocationHours,
  fullName,
  isAllocationActive,
  normalizePayFrequency,
  projectIdFromName,
  salaryFromRate,
  type ProjectAllocation,
} from "@torre/team";
import { localSlot } from "@torre/work-hours";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  MEMBER_PAY_MODE_OPTIONS,
  modeLabel,
  PAY_FREQUENCY_OPTIONS,
  payFrequencyLabel,
  ROLE_OPTIONS,
  roleLabel,
  ROLE_TONES,
  CURRENCY_OPTIONS,
  TEAM_MEMBER_STATUS_OPTIONS,
  teamMemberStatusLabel,
  TEAM_MEMBER_STATUS_TONES,
  formatMoney,
} from "../theme";
import {
  fetchTeamMembers,
  fetchPayRates,
  fetchTeamBonuses,
  fetchSquads,
  createTeamMember,
  updateTeamMember,
  deleteTeamMember,
} from "../services/teamService";
import type { PayRate, Squad, TeamMember, TeamMemberBonus } from "../types";
import PersonAvatar from "../../../components/staff-ops/PersonAvatar";
import KpiStrip, { type KpiItem } from "../../../components/staff-ops/KpiStrip";
import { tenure } from "../../../components/staff-ops/relative";
import MemberDetailDrawer from "../components/MemberDetailDrawer";
import { useStaffAccess } from "../../../hooks/useIsStaffAdmin";
import ClientScopeSelect from "../../work-hours/components/ClientScopeSelect";
import { memberSalary, salariesExistHere } from "../salary";

// Registro y clasificación de colaboradores (AgroCloud#11). Alta/edición con datos
// personales, de contacto y previsionales, clasificación reutilizable en
// tareas, listado + búsqueda; sobre el shell CRUD (con toggle grid/list).
export default function TeamMembersSection() {
  const { t } = useTranslation("common");
  // Sueldo y datos fiscales sólo para quien administra (#742). El servidor ya
  // no los manda ni los acepta de nadie más; aquí se evita enseñar campos
  // vacíos que además responderían 403 al guardar.
  const { isStaffAdmin, ownerPolicy } = useStaffAccess();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  // Clave i18n, no texto: `t` no es estable entre renders (react-i18next la
  // reemplaza al cargar un namespace o al cambiar de idioma), así que tenerla en
  // las dependencias de `load` recargaba la lista sin motivo.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  // #979: en CC-CONSOLE se ve todo el equipo; el admin puede acotar a un cliente.
  const [clientId, setClientId] = useState("");
  // #1595: ficha lateral, edición pedida desde ella y filtros del directorio.
  const [selected, setSelected] = useState<Row | null>(null);
  const [editRequest, setEditRequest] = useState<Row | null>(null);
  const clearEditRequest = useCallback(() => setEditRequest(null), []);
  const [scope, setScope] = useState<{ area: string | null; squad: string | null }>({ area: null, squad: null });
  const [quick, setQuick] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorKey(null);
    try {
      const { items } = await fetchTeamMembers(clientId ? { clientId } : {});
      // #1595: la cuadrilla de cada quien sale de la ficha o, si no la trae, de
      // la lista de integrantes de cada cuadrilla. Si las cuadrillas no se
      // pueden leer, el directorio sigue sin ese dato.
      let squads: Squad[] = [];
      try {
        squads = (await fetchSquads({ limit: 500 })).items ?? [];
      } catch {
        squads = [];
      }
      const squadOf = new Map<string, Squad>();
      for (const sq of squads) for (const m of sq.members ?? []) if (m?.teamMemberId && !squadOf.has(m.teamMemberId)) squadOf.set(m.teamMemberId, sq);
      // `fullName` alimenta la columna principal y la búsqueda.
      setRows(
        items.map((w) => {
          const ext = w as TeamMember & Record<string, unknown>;
          const sq = squadOf.get(w.id) as (Squad & { area?: string | null }) | undefined;
          return {
            ...w,
            fullName: fullName(w),
            squadId: ext.squadId ?? sq?.id ?? null,
            squadName: ext.squadName ?? sq?.name ?? null,
            area: ext.area ?? sq?.area ?? null,
          };
        }) as unknown as Row[],
      );
    } catch {
      setErrorKey("team.loadError");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  // #1296: los rangos de sueldo (tarifas de sueldo fijo activas) a los que se
  // puede ligar una ficha. Sólo quien administra edita fichas, así que sólo a
  // esa persona se le pide el catálogo; si falla, el formulario sigue sirviendo
  // sin rango (sueldo a mano), no se rompe el alta.
  const [bands, setBands] = useState<PayRate[]>([]);
  useEffect(() => {
    if (!isStaffAdmin) return;
    let alive = true;
    (async () => {
      try {
        const { items } = await fetchPayRates({ mode: "fixed_salary", active: "true" });
        if (alive) setBands(items ?? []);
      } catch {
        if (alive) setBands([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isStaffAdmin]);

  // #1365: bonos vigentes hoy, para la columna «Bono vigente» (sólo quien administra).
  const [bonuses, setBonuses] = useState<TeamMemberBonus[]>([]);
  useEffect(() => {
    if (!isStaffAdmin) return;
    let alive = true;
    (async () => {
      try {
        const todayIso = localSlot(new Date().toISOString())?.date ?? new Date().toISOString().slice(0, 10);
        const { items } = await fetchTeamBonuses({ activeOn: todayIso });
        if (alive) setBonuses(items ?? []);
      } catch {
        if (alive) setBonuses([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isStaffAdmin]);

  /** Puntito del avatar: activo en línea, inactivo ausente, baja apagado. */
  const presenceOf = (r: Row) => (r.status === "inactive" ? "away" : r.status === "offboarded" ? "offline" : "online") as "online" | "away" | "offline";

  /** «Tech Lead · MXN 40,000 al mes»: cómo se nombra un rango en el selector. */
  const bandLabel = (b: PayRate): string =>
    `${b.name} · ${formatMoney(b.fixedSalary, b.currency ?? "MXN")} ${t(`team.salary.per.${normalizePayFrequency(b.payFrequency)}`)}`;

  /** Valores de la ficha que salen del rango elegido (los mismos que copia el servidor). */
  const fieldsFromBand = (band: PayRate | undefined, frequency: string): Record<string, string> | undefined => {
    if (!band) return undefined;
    const amount = salaryFromRate(band, frequency);
    return {
      payMode: "fixed_salary",
      currency: band.currency ?? "",
      fixedSalary: amount == null ? "" : String(amount),
      // #1364: el rango es de un puesto; la ficha toma ese puesto.
      ...(band.position ? { role: band.position } : {}),
    };
  };

  /**
   * Rangos que se ofrecen para un puesto (#1364): los de ese puesto, los que no
   * declaran puesto y el que ya tenga la ficha (para no esconder lo vigente).
   */
  const bandsForRole = (role: string, current: string): PayRate[] =>
    bands.filter((b) => !b.position || b.position === role || b.id === current);

  const allColumns: Column[] = [
    {
      key: "fullName",
      label: t("team.fields.fullName"),
      // #1595: avatar de iniciales con su color, y el puesto debajo.
      render: (r) => (
        <span className="flex items-center gap-2">
          <PersonAvatar name={String(r.fullName ?? "")} color={r.avatarColor as string | null} size="sm" presence={presenceOf(r)} />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="font-medium">{String(r.fullName ?? "")}</span>
            {r.jobTitle ? <span className="truncate text-[11px] opacity-70">{String(r.jobTitle)}</span> : null}
          </span>
        </span>
      ),
    },
    { key: "taxId", label: t("team.fields.taxId") },
    {
      key: "role",
      label: t("team.fields.role"),
      render: (r) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            ROLE_TONES[String(r.role)] ?? "bg-gray-400/15 text-gray-500"
          }`}
        >
          {roleLabel(t, r.role)}
        </span>
      ),
    },
    { key: "phone", label: t("team.fields.phone") },
    { key: "pensionFund", label: t("team.fields.pensionFund") },
    {
      key: "projectAllocations",
      label: t("team.fields.allocations"),
      render: (r) => {
        const list = (r.projectAllocations as ProjectAllocation[] | undefined) ?? [];
        if (!list.length) return "—";
        // Las asignaciones con vigencia se marcan: no son permanentes.
        return list
          .map((a) => {
            const validity = a.startDate || a.endDate ? ` (${a.startDate ?? "—"} → ${a.endDate ?? "—"})` : "";
            return `${a.percentage}% ${a.projectName}${validity}`;
          })
          .join(" · ");
      },
    },
    {
      key: "payMode",
      label: t("team.fields.payMode"),
      render: (r) => modeLabel(t, r.payMode ?? "daily"),
    },
    {
      // #1296: el rango al que pertenece la persona; es lo que liga el padrón
      // con el catálogo de tarifas. Sin rango: sueldo capturado a mano.
      key: "payRateName",
      label: t("team.fields.payRate"),
      render: (r) =>
        r.payRateName ? (
          <span
            data-testid="salary-band"
            className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-500"
          >
            {String(r.payRateName)}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "dailyRate",
      label: t("team.fields.dailyRate"),
      align: "right",
      render: (r) => {
        if (r.payMode === "hourly") return r.hourlyRate ? `${formatMoney(Number(r.hourlyRate))} / h` : "—";
        if (r.payMode === "fixed_salary") return r.fixedSalary ? `${formatMoney(Number(r.fixedSalary))} / periodo` : "—";
        return r.dailyRate ? `${formatMoney(Number(r.dailyRate))} / día` : "—";
      },
    },
    {
      // #1145: el sueldo registrado de cada quien, a la vista de los dueños.
      // Sale de `fixedSalary` (lo de un periodo) sea cual sea la modalidad:
      // la columna de tarifa de arriba sólo lo enseñaba con «sueldo fijo».
      key: "salary",
      label: t("team.salary.column"),
      align: "right",
      render: (r) => {
        const s = memberSalary(r);
        if (!s) {
          return (
            <span data-testid="salary-none" className="text-xs italic text-gray-500">
              {t("team.salary.none")}
            </span>
          );
        }
        return (
          <span data-testid="salary" className="inline-flex flex-col items-end leading-tight">
            <span className="font-medium">
              {formatMoney(s.amount, s.currency)}{" "}
              <span className="text-xs font-normal opacity-70">{t(`team.salary.per.${s.frequency}`)}</span>
            </span>
            {s.frequency !== "monthly" && (
              <span className="text-[11px] opacity-70">
                {t("team.salary.monthly", { amount: formatMoney(s.monthly, s.currency) })}
              </span>
            )}
          </span>
        );
      },
    },
    {
      // #1365: los bonos vigentes hoy de cada quien; el monto es por periodo o
      // el total del rango, que es como se capturó.
      key: "bonuses",
      label: t("team.bonuses.column"),
      render: (r) => {
        const mine = bonuses.filter((b) => b.teamMemberId === r.id);
        if (!mine.length) return "—";
        return (
          <span data-testid="member-bonus" className="inline-flex flex-col leading-tight">
            {mine.map((b) => (
              <span key={b.id} className="text-xs">
                {b.concept} · {t(b.mode === "range" ? "team.bonuses.rangeShort" : "team.bonuses.perPeriodShort", { amount: formatMoney(b.amount, b.currency ?? null) })}
              </span>
            ))}
          </span>
        );
      },
    },
    {
      key: "status",
      label: t("team.fields.status"),
      align: "center",
      render: (r) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            TEAM_MEMBER_STATUS_TONES[String(r.status)] ?? "bg-gray-400/15 text-gray-500"
          }`}
        >
          {teamMemberStatusLabel(t, r.status)}
        </span>
      ),
    },
  ];

  // En CC-CONSOLE quien no administra solo recibe SU ficha, completa (#1007):
  // ve sus propias columnas. En otros tenants recibe el padrón sin RFC ni datos
  // previsionales (#742), así que esas columnas saldrían vacías.
  // El sueldo sólo para quien administra (en CC-CONSOLE, los dueños): a los
  // demás el servidor no lo manda, y la columna diría «sin sueldo» sin serlo.
  const columns = (
    isStaffAdmin || ownerPolicy
      ? allColumns
      : allColumns.filter((c) => !["taxId", "pensionFund"].includes(c.key))
  ).filter((c) => (c.key !== "salary" && c.key !== "bonuses") || isStaffAdmin);

  // #1145: los sueldos reales solo existen en producción; en dev/QA van nulos
  // o de mentira, y se avisa para que nadie lea un «sin sueldo» como un dato.
  const salaryNotice =
    isStaffAdmin && !salariesExistHere() ? (
      <span
        data-testid="salary-prod-only"
        className="inline-flex items-center gap-1 rounded-lg border border-amber-400/50 bg-amber-400/10 px-2 py-1 text-xs text-amber-600"
      >
        <i className="ri-information-line" aria-hidden="true" />
        {t("team.salary.prodOnly")}
      </span>
    ) : null;

  const sensitiveFields = new Set([
    "taxId",
    "currency",
    "payRateId",
    "payFrequency",
    "hourlyRate",
    "fixedSalary",
    "dailyRate",
    "pensionFund",
    "health",
    "contractNumber",
    "address",
  ]);

  const allFields: FormField[] = [
    { key: "name", label: t("team.fields.name"), required: true },
    { key: "lastName", label: t("team.fields.lastName") },
    { key: "taxId", label: t("team.fields.taxId") },
    {
      key: "role",
      label: t("team.fields.role"),
      type: "select",
      required: true,
      defaultValue: "freelancer",
      options: ROLE_OPTIONS.map((c) => ({
        value: c,
        label: roleLabel(t, c),
      })),
      hint: t("team.hints.role"),
      // #1364: al cambiar de puesto, el rango sigue al puesto: si el puesto tiene
      // un solo rango activo se propone solo; si el rango vigente es de otro
      // puesto, se quita (el sueldo capturado se conserva hasta elegir otro).
      derive: (value, values) => {
        const current = bands.find((b) => b.id === values.payRateId);
        if (current && current.position && current.position !== value) {
          const candidates = bands.filter((b) => b.position === value);
          if (candidates.length === 1) return { payRateId: candidates[0].id, ...fieldsFromBand(candidates[0], values.payFrequency), role: value };
          return { payRateId: "" };
        }
        if (!values.payRateId) {
          const candidates = bands.filter((b) => b.position === value);
          if (candidates.length === 1) return { payRateId: candidates[0].id, ...fieldsFromBand(candidates[0], values.payFrequency), role: value };
        }
        return undefined;
      },
    },
    {
      key: "status",
      label: t("team.fields.status"),
      type: "select",
      defaultValue: "active",
      options: TEAM_MEMBER_STATUS_OPTIONS.map((e) => ({
        value: e,
        label: teamMemberStatusLabel(t, e),
      })),
    },
    { key: "email", label: t("team.fields.email") },
    { key: "phone", label: t("team.fields.phone") },
    { key: "address", label: t("team.fields.address") },
    { key: "hireDate", label: t("team.fields.hireDate"), type: "date" },
    { key: "pensionFund", label: t("team.fields.pensionFund") },
    { key: "health", label: t("team.fields.health") },
    { key: "contractNumber", label: t("team.fields.contractNumber") },
    {
      key: "payMode",
      label: t("team.fields.payMode"),
      type: "select",
      defaultValue: "daily",
      options: MEMBER_PAY_MODE_OPTIONS.map((m) => ({ value: m, label: modeLabel(t, m) })),
      hint: t("team.hints.payMode"),
    },
    {
      // #1296: el rango de sueldo. Elegirlo rellena modalidad, moneda y sueldo
      // (convertido a la periodicidad de la persona); el servidor hace lo mismo
      // al guardar y cada vez que el rango cambia, así que lo rellenado aquí es
      // una vista previa, no la fuente.
      key: "payRateId",
      label: t("team.fields.payRate"),
      type: "select",
      // #1364: sólo los rangos del puesto elegido (y el vigente).
      optionsFor: (values) =>
        bandsForRole(values.role || "", values.payRateId || "").map((b) => ({ value: b.id, label: bandLabel(b) })),
      hint: t("team.hints.payRate"),
      derive: (value, values) => fieldsFromBand(bands.find((b) => b.id === value), values.payFrequency),
    },
    {
      // Cada cuánto cobra la persona: `fixedSalary` es lo de UN periodo de esto.
      key: "payFrequency",
      label: t("team.fields.payFrequency"),
      type: "select",
      defaultValue: "semimonthly",
      options: PAY_FREQUENCY_OPTIONS.map((f) => ({ value: f, label: payFrequencyLabel(t, f) })),
      hint: t("team.hints.payFrequency"),
      // Cambiar la periodicidad de alguien ligado a un rango reconvierte su sueldo.
      derive: (value, values) => {
        const band = bands.find((b) => b.id === values.payRateId);
        if (!band) return undefined;
        const amount = salaryFromRate(band, value);
        return amount == null ? undefined : { fixedSalary: String(amount) };
      },
    },
    {
      // Sin moneda por persona, «20 000» y «300» se pintan igual y se suman
      // entre sí. El campo admite cualquier código ISO; la lista corta son las
      // dos que la empresa usa hoy (#741).
      key: "currency",
      label: t("team.fields.currency"),
      type: "select",
      options: [
        { value: "", label: t("team.currencyUnset") },
        ...CURRENCY_OPTIONS.map((c) => ({ value: c, label: c })),
      ],
      hint: t("team.hints.currency"),
    },
    {
      key: "hourlyRate",
      label: t("team.fields.hourlyRate"),
      type: "number",
      hint: t("team.hints.memberHourlyRate"),
      visibleWhen: (v) => v.payMode === "hourly",
    },
    {
      key: "fixedSalary",
      label: t("team.fields.fixedSalary"),
      type: "number",
      hint: t("team.hints.memberFixedSalary"),
      // #1145: también se enseña si ya hay un sueldo registrado, sea cual sea la
      // modalidad: la ficha es donde el dueño lo revisa y lo corrige.
      visibleWhen: (v) => v.payMode === "fixed_salary" || Boolean(v.fixedSalary),
    },
    {
      // #1487: solo al cambiar el sueldo; alimentan el historial de sueldos de Nómina.
      key: "salaryEffectiveFrom",
      label: t("team.fields.salaryEffectiveFrom"),
      type: "date",
      hint: t("team.hints.salaryEffectiveFrom"),
      visibleWhen: (v) => v.payMode === "fixed_salary" || Boolean(v.fixedSalary),
    },
    {
      key: "salaryChangeReason",
      label: t("team.fields.salaryChangeReason"),
      hint: t("team.hints.salaryChangeReason"),
      visibleWhen: (v) => v.payMode === "fixed_salary" || Boolean(v.fixedSalary),
    },
    {
      key: "dailyRate",
      label: t("team.fields.dailyRate"),
      type: "number",
      hint: t("team.hints.dailyRate"),
      visibleWhen: (v) => (v.payMode || "daily") === "daily",
    },
    {
      key: "weeklyCapacityHours",
      label: t("team.fields.weeklyCapacityHours"),
      type: "number",
      defaultValue: 40,
      hint: t("team.hints.weeklyCapacityHours"),
    },
    {
      key: "projectAllocations",
      label: t("team.fields.allocations"),
      type: "repeat",
      addRowLabel: t("team.addAllocation"),
      hint: t("team.hints.allocations"),
      repeatColumns: [
        { key: "projectName", label: t("team.fields.projectName") },
        { key: "percentage", label: "%", type: "number" },
        { key: "repos", label: t("team.fields.repos") },
        // Vigencia: el backend la usa para el planeado vs real. Sin estos campos,
        // editar cualquier dato del colaborador volvía permanente una asignación
        // temporal creada por API.
        { key: "startDate", label: t("team.fields.startDate") },
        { key: "endDate", label: t("team.fields.endDate") },
      ],
    },
    { key: "notes", label: t("team.fields.notes"), type: "textarea" },
  ];

  const fields = isStaffAdmin ? allFields : allFields.filter((f) => !sensitiveFields.has(f.key));

  /** Fecha de hoy en CDMX (la misma referencia que usa el backend para la vigencia). */
  const todayLocal = (): string => localSlot(new Date().toISOString())?.date ?? new Date().toISOString().slice(0, 10);

  /** `YYYY-MM-DD` o null: el backend solo acepta fechas ISO o ausencia de vigencia. */
  const parseDate = (v: unknown): string | null => {
    const s = String(v ?? "").trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  };

  /** Asignaciones del formulario (JSON del campo repeat) → contrato de @torre/team. */
  const parseAllocations = (raw: string | undefined): ProjectAllocation[] => {
    try {
      const rows = JSON.parse(raw || "[]") as Record<string, unknown>[];
      return rows
        .filter((row) => String(row.projectName ?? "").trim())
        .map((row) => ({
          projectId: String(row.projectId || projectIdFromName(String(row.projectName))),
          projectName: String(row.projectName).trim(),
          percentage: Number(row.percentage) || 0,
          repos: (Array.isArray(row.repos) ? row.repos : String(row.repos ?? "").split(/[,\s]+/))
            .map((x) => String(x).trim())
            .filter((x) => x.includes("/")),
          // La vigencia viaja de ida y vuelta: editar al colaborador ya no la borra.
          startDate: parseDate(row.startDate),
          endDate: parseDate(row.endDate),
        }));
    } catch {
      return [];
    }
  };

  /**
   * Máximo simultáneo del reparto. El backend NO valida la suma de todo, sino la
   * suma por tramos de vigencia: el máximo se alcanza en algún inicio de vigencia
   * (o «desde siempre», para las asignaciones sin fecha).
   */
  const peakAllocated = (list: ProjectAllocation[]): number => {
    const checkpoints = new Set<string>(["0000-01-01"]);
    for (const a of list) if (a.startDate) checkpoints.add(a.startDate);
    let peak = 0;
    for (const date of checkpoints) {
      const total = list.filter((a) => isAllocationActive(a, date)).reduce((acc, a) => acc + (Number(a.percentage) || 0), 0);
      if (total > peak) peak = total;
    }
    return Math.round(peak * 100) / 100;
  };

  // Resumen en vivo del reparto: horas por semana de cada proyecto y lo libre.
  // El total que se muestra es el VIGENTE hoy (no la suma de todo), igual que
  // lo mide el backend; aparte se avisa del máximo simultáneo si pasa de 100 %.
  const formFooter = (values: Record<string, string>) => {
    const allocations = parseAllocations(values.projectAllocations);
    if (!allocations.length) return null;
    const capacity = Number(values.weeklyCapacityHours) || 40;
    const date = todayLocal();
    const { allocated, free } = allocatedPercentage(allocations, date);
    const peak = peakAllocated(allocations);
    const hours = computeAllocationHours(capacity, allocations, date);
    const activeIds = new Set(hours.map((a) => a.projectId));
    return (
      <div
        data-testid="allocation-footer"
        className={`rounded-xl border p-3 text-xs ${peak > 100 ? "border-red-500 text-red-500" : "border-violet-400/40"}`}
      >
        <p className="mb-1 font-semibold">
          {t("team.allocationTotal", { total: allocated, free, capacity })}
        </p>
        <p className="mb-1 opacity-80">{t("team.allocationTotalHint", { date })}</p>
        <ul className="space-y-0.5">
          {allocations.map((a) => {
            const active = activeIds.has(a.projectId);
            const weekly = hours.find((h) => h.projectId === a.projectId)?.expectedWeeklyHours;
            const validity =
              a.startDate || a.endDate
                ? ` · ${t("team.allocationValidity", { from: a.startDate ?? "—", to: a.endDate ?? "—" })}`
                : "";
            return (
              <li key={a.projectId} className={active ? "" : "opacity-60"}>
                {a.projectName}: {a.percentage}%
                {active ? ` → ${weekly} h/sem` : ` · ${t("team.allocationNotActive")}`}
                {validity}
              </li>
            );
          })}
        </ul>
        {peak > 100 && (
          <p className="mt-1">
            {t("team.allocationOver")} {t("team.allocationPeak", { peak })}
          </p>
        )}
      </div>
    );
  };

  const toPayload = (values: Record<string, string>): Partial<TeamMember> => ({
    name: values.name,
    lastName: values.lastName || null,
    taxId: values.taxId || null,
    role: (values.role || "freelancer") as TeamMember["role"],
    status: (values.status || "active") as TeamMember["status"],
    email: values.email || null,
    phone: values.phone || null,
    address: values.address || null,
    hireDate: values.hireDate || null,
    pensionFund: values.pensionFund || null,
    health: values.health || null,
    contractNumber: values.contractNumber || null,
    payMode: (values.payMode || "daily") as TeamMember["payMode"],
    currency: values.currency || null,
    // #1296: periodicidad y rango. Un rango vacío desliga (null), no «sin cambio».
    payFrequency: normalizePayFrequency(values.payFrequency),
    payRateId: values.payRateId || null,
    dailyRate: values.dailyRate ? Number(values.dailyRate) : null,
    hourlyRate: values.hourlyRate ? Number(values.hourlyRate) : null,
    fixedSalary: values.fixedSalary ? Number(values.fixedSalary) : null,
    weeklyCapacityHours: values.weeklyCapacityHours ? Number(values.weeklyCapacityHours) : 40,
    projectAllocations: parseAllocations(values.projectAllocations),
    notes: values.notes || null,
    // #1487: fecha y motivo del cambio de sueldo, solo si se capturaron (van a la bitácora, no a la ficha).
    ...((values.salaryEffectiveFrom || values.salaryChangeReason
      ? { salaryEffectiveFrom: values.salaryEffectiveFrom || null, salaryChangeReason: values.salaryChangeReason || null }
      : {}) as unknown as Partial<TeamMember>),
  });

  // --- Directorio tipo consola (#1595): indicadores, árbol y ficha lateral ---
  const noArea = t("staffOps.sidebar.noArea");
  const areaOf = (r: Row) => String(r.area || noArea);
  const tree = useMemo(() => {
    const areas = new Map<string, { count: number; squads: Map<string, { name: string; count: number }> }>();
    for (const r of rows) {
      const area = areaOf(r);
      const a = areas.get(area) ?? { count: 0, squads: new Map() };
      a.count += 1;
      if (r.squadId) {
        const sq = a.squads.get(String(r.squadId)) ?? { name: String(r.squadName || r.squadId), count: 0 };
        sq.count += 1;
        a.squads.set(String(r.squadId), sq);
      }
      areas.set(area, a);
    }
    return [...areas.entries()].sort((x, y) => x[0].localeCompare(y[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, noArea]);

  const newSince = Date.now() - 90 * 86400000;
  const isNew = (r: Row) => Boolean(r.hireDate) && Date.parse(`${String(r.hireDate)}T12:00:00Z`) >= newSince;
  const quickFilters: Record<string, (r: Row) => boolean> = {
    active: (r) => (r.status || "active") === "active",
    payroll: (r) => r.employmentType !== "contractor",
    contractor: (r) => r.employmentType === "contractor",
    recent: isNew,
  };
  const visibleRows = rows.filter(
    (r) => (!scope.area || areaOf(r) === scope.area) && (!scope.squad || String(r.squadId) === scope.squad) && (!quick || quickFilters[quick]?.(r)),
  );

  const toggleQuick = (id: string) => setQuick((q) => (q === id ? null : id));
  const count = (f: (r: Row) => boolean) => rows.filter(f).length;
  const squadsCount = new Set(rows.map((r) => r.squadId).filter(Boolean)).size;
  const activeCount = count(quickFilters.active);
  const kpis: KpiItem[] = [
    { id: "total", label: t("staffOps.kpi.people"), value: rows.length, icon: "ri-team-line", tone: "primary", hint: t("staffOps.kpi.areas", { count: tree.length }), onClick: () => setQuick(null), active: quick === null },
    { id: "active", label: t("staffOps.kpi.active"), value: activeCount, icon: "ri-user-follow-line", tone: "success", progress: rows.length ? activeCount / rows.length : null, onClick: () => toggleQuick("active"), active: quick === "active" },
    { id: "payroll", label: t("staffOps.kpi.payroll"), value: count(quickFilters.payroll), icon: "ri-id-card-line", tone: "info", onClick: () => toggleQuick("payroll"), active: quick === "payroll" },
    { id: "contractor", label: t("staffOps.kpi.contractors"), value: count(quickFilters.contractor), icon: "ri-briefcase-4-line", tone: "warning", onClick: () => toggleQuick("contractor"), active: quick === "contractor" },
    { id: "squads", label: t("staffOps.kpi.squads"), value: squadsCount, icon: "ri-group-2-line", tone: "violet" },
    { id: "recent", label: t("staffOps.kpi.recent"), value: count(isNew), icon: "ri-user-add-line", tone: "neutral", hint: t("staffOps.kpi.recentHint"), onClick: () => toggleQuick("recent"), active: quick === "recent" },
  ];

  const treeBtn = (active: boolean) =>
    `flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition ${active ? "bg-primary-500/15 font-semibold text-primary-500" : "text-content hover:bg-subtle"}`;
  const sidebar = (
    <nav aria-label={t("staffOps.sidebar.title")} data-testid="directory-tree">
      <button type="button" className={treeBtn(!scope.area && !scope.squad)} onClick={() => setScope({ area: null, squad: null })}>
        <span>
          <i className="ri-building-2-line mr-1.5" aria-hidden="true" />
          {t("staffOps.sidebar.all")}
        </span>
        <span className="text-[10px] opacity-70">{rows.length}</span>
      </button>
      <ul className="mt-1 space-y-0.5">
        {tree.map(([area, info]) => (
          <li key={area}>
            <button type="button" className={treeBtn(scope.area === area && !scope.squad)} onClick={() => setScope({ area, squad: null })} data-testid={`tree-area-${area}`}>
              <span className="truncate">
                <i className="ri-folder-2-line mr-1.5" aria-hidden="true" />
                {area}
              </span>
              <span className="text-[10px] opacity-70">{info.count}</span>
            </button>
            {info.squads.size > 0 && (
              <ul className="ml-4 border-l border-line pl-1">
                {[...info.squads.entries()].map(([sid, sq]) => (
                  <li key={sid}>
                    <button type="button" className={treeBtn(scope.squad === sid)} onClick={() => setScope({ area, squad: sid })} data-testid={`tree-squad-${sid}`}>
                      <span className="truncate">
                        <i className="ri-group-line mr-1.5" aria-hidden="true" />
                        {sq.name}
                      </span>
                      <span className="text-[10px] opacity-70">{sq.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );

  const memberCard = (r: Row) => {
    const status = String(r.status || "active");
    const ten = tenure(r.hireDate as string | null);
    return (
      <article className="h-full rounded-xl border border-line bg-card p-4 shadow-sm transition hover:border-primary-500/60 hover:shadow" data-testid={`member-card-${r.id}`}>
        <div className="flex items-start gap-3">
          <PersonAvatar name={String(r.fullName ?? "")} color={r.avatarColor as string | null} size="md" presence={presenceOf(r)} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-content">{String(r.fullName ?? "")}</p>
            <p className="truncate text-xs text-content-secondary">{String(r.jobTitle || roleLabel(t, r.role))}</p>
          </div>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${TEAM_MEMBER_STATUS_TONES[status] ?? "bg-subtle text-content-secondary"}`}>{teamMemberStatusLabel(t, status)}</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
          <span className={`rounded-full px-2 py-0.5 ${ROLE_TONES[String(r.role)] ?? "bg-subtle text-content-secondary"}`}>{roleLabel(t, r.role)}</span>
          {r.squadName ? <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-violet-500">{String(r.squadName)}</span> : null}
          {r.employmentType ? <span className="rounded-full bg-subtle px-2 py-0.5 text-content-secondary">{t(`staffOps.employmentType.${String(r.employmentType)}`)}</span> : null}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
          <dt className="text-content-muted">{t("staffOps.fields.area")}</dt>
          <dd className="truncate text-right text-content">{String(r.area || "—")}</dd>
          <dt className="text-content-muted">{t("staffOps.fields.location")}</dt>
          <dd className="truncate text-right text-content">{String(r.locationName || "—")}</dd>
          <dt className="text-content-muted">{t("staffOps.fields.tenure")}</dt>
          <dd className="truncate text-right text-content">{ten ? t("staffOps.drawer.tenure", { years: ten.years, months: ten.months }) : "—"}</dd>
        </dl>
      </article>
    );
  };

  return (
    <>
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("team.menu.teamMembers")}
      subtitle={t("team.teamMembersSubtitle")}
      icon="ri-team-line"
      columns={columns}
      fields={fields}
      formFooter={formFooter}
      rows={visibleRows}
      loading={loading}
      error={errorKey ? t(errorKey) : null}
      searchKey="fullName"
      header={<KpiStrip items={kpis} loading={loading} />}
      sidebar={rows.length ? sidebar : undefined}
      gridCard={memberCard}
      onRowClick={setSelected}
      editRequest={editRequest}
      onEditRequestHandled={clearEditRequest}
      toolbar={
        isStaffAdmin ? (
          <>
            {salaryNotice}
            <ClientScopeSelect value={clientId} onChange={setClientId} />
          </>
        ) : undefined
      }
      addLabel={t("team.newTeamMember")}
      onCreate={async (values) => {
        await createTeamMember(toPayload(values));
        await load();
      }}
      onUpdate={async (id, values) => {
        await updateTeamMember(id, toPayload(values));
        await load();
      }}
      onDelete={async (id) => {
        await deleteTeamMember(id);
        await load();
      }}
    />
    <MemberDetailDrawer
      member={selected}
      onClose={() => setSelected(null)}
      canSeePay={isStaffAdmin}
      onEdit={
        isStaffAdmin
          ? (m) => {
              setSelected(null);
              setEditRequest(m);
            }
          : undefined
      }
    />
    </>
  );
}
